<template>
  <DashboardSection
    title="Kanalübergreifende Timeline"
    subtitle="Zeitraum mit Maßnahmen · Balken führen zur gefilterten Detailseite"
    :empty="!lanes.length"
    empty-text="Keine Maßnahmen im Zeitraum."
  >
    <div ref="root" class="mb-swim">
      <div class="mb-swim__row mb-swim__row--axis">
        <div class="mb-swim__label" />
        <div class="mb-swim__track mb-swim__track--axis">
          <span
            v-for="tick in visibleTicks"
            :key="tick.key"
            class="mb-swim__tick"
            :style="{ left: tick.left }"
          >
            {{ tick.label }}
          </span>
        </div>
      </div>
      <div v-for="lane in lanes" :key="lane.code" class="mb-swim__row">
        <router-link :to="lane.to" class="mb-swim__label" :style="{ '--mb-lane': lane.color }">
          <span class="mb-swim__lane-dot" aria-hidden="true" />{{ lane.label }}
        </router-link>
        <!-- UX: Clip-Bereich – Balken ragen nicht mehr über den Rand (vorher: Print-Balken). -->
        <div
          class="mb-swim__track"
          :style="{ height: `${lane.trackCount * 28 + 4}px` }"
        >
          <span
            v-for="tick in ticks"
            :key="`${lane.code}-${tick.key}`"
            class="mb-swim__gridline"
            :style="{ left: tick.left }"
          />
          <router-link
            v-for="bar in lane.bars"
            :key="bar.key"
            :to="bar.to"
            class="mb-swim__bar"
            :style="{
              left: bar.left,
              width: bar.width,
              top: bar.top,
              background: lane.color
            }"
            :aria-label="bar.tooltip"
            @mouseenter="showTip(bar, $event)"
            @mousemove="moveTip($event)"
            @mouseleave="hideTip"
            @focus="showTip(bar, $event)"
            @blur="hideTip"
          >
            <!-- UX: Balkentext nur, wenn genug Platz ist – sonst steht er im Tooltip.
                 Vorher: "2 Ma", "Men", "fol_k", "AU 0". -->
            <span v-if="fits(bar)" class="mb-swim__bar-text">{{ bar.label }}</span>
          </router-link>
        </div>
      </div>
    </div>
    <!-- Eigener Tooltip statt title-Attribut: erscheint sofort (native Tooltips erst nach ~1 s). -->
    <Teleport to="body">
      <div v-if="tip" class="mb-swim-tip" role="tooltip" :style="{ left: `${tip.x}px`, top: `${tip.y}px` }">
        <p class="mb-swim-tip__title">{{ tip.lines[0] }}</p>
        <p v-for="(line, index) in tip.lines.slice(1)" :key="index" class="mb-swim-tip__line">{{ line }}</p>
      </div>
    </Teleport>
  </DashboardSection>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  CHANNEL_ROUTES,
  addDays,
  endOfMonth,
  formatDate,
  isoDate,
  parseDate,
  startOfMonth
} from '../composables/formatters';
import { CHANNEL_COLORS } from '../composables/designTokens';
import { humanizeName, mailingDisplayNames } from '../composables/displayNames';
import { useMarketingFilters } from '../composables/useMarketingFilters';
import DashboardSection from './DashboardSection.vue';

const props = defineProps({
  items: { type: Array, default: () => [] },
  // UX: Kurztitel → voller Titel ("AU" → "Apotheken Umschau"), z. B. aus filterOptions.print_titles.
  printTitles: { type: Array, default: () => [] }
});

const { queryFor } = useMarketingFilters();

const CHANNEL_ORDER = [
  { code: 'digital', label: 'Digitale Kampagnen' },
  { code: 'social', label: 'Social Media' },
  { code: 'email', label: 'E-Mail' },
  { code: 'traffic', label: 'Website-Traffic' },
  { code: 'print', label: 'Print' }
];

const LABEL_COL_PX = 172;
const MIN_BAR_PX = 10;
const MIN_TICK_GAP_PX = 56;
const CHAR_PX = 6.2;

// Gemessene Spurbreite in px, damit Balkentext und Monatsköpfe nur bei genug Platz erscheinen.
const root = ref(null);
const trackPx = ref(800);
let observer = null;

function measure() {
  const width = root.value?.clientWidth || 0;
  if (width) trackPx.value = Math.max(120, width - LABEL_COL_PX - 32);
}

onMounted(() => {
  measure();
  if (typeof ResizeObserver !== 'undefined' && root.value) {
    observer = new ResizeObserver(measure);
    observer.observe(root.value);
  }
});

onBeforeUnmount(() => observer?.disconnect());

// Sofort-Tooltip für die Balken. Position folgt der Maus, bei Tastatur-Fokus unter dem Balken.
const tip = ref(null);
const TIP_OFFSET = 14;
const TIP_WIDTH = 320;

function tipPosition(event) {
  if (event.type === 'focus') {
    const rect = event.target.getBoundingClientRect();
    return { x: rect.left, y: rect.bottom + 6 };
  }
  const x = Math.min(event.clientX + TIP_OFFSET, window.innerWidth - TIP_WIDTH - 12);
  return { x: Math.max(12, x), y: event.clientY + TIP_OFFSET };
}

function showTip(bar, event) {
  tip.value = { lines: String(bar.tooltip || '').split('\n').filter(Boolean), ...tipPosition(event) };
}

function moveTip(event) {
  if (tip.value) Object.assign(tip.value, tipPosition(event));
}

function hideTip() {
  tip.value = null;
}

const datedItems = computed(() => (props.items || []).filter((item) => parseDate(item.date)));

// UX: Zeitraum = Datenzeitraum (auf ganze Monate gerundet) statt des gesamten Filterjahres.
const bounds = computed(() => {
  const times = [];
  datedItems.value.forEach((item) => {
    const start = parseDate(item.date);
    const end = parseDate(item.end_date) || start;
    if (start) times.push(start.getTime());
    if (end) times.push(end.getTime());
  });
  if (!times.length) return null;
  const start = startOfMonth(new Date(Math.min(...times)));
  const end = endOfMonth(new Date(Math.max(...times)));
  const min = start.getTime();
  const max = Math.max(addDays(end, 1).getTime(), min + 86400000);
  return { start, end, min, max, span: max - min };
});

const ticks = computed(() => {
  if (!bounds.value) return [];
  const { start, end, min, span } = bounds.value;
  const marks = [];
  let cursor = startOfMonth(start);
  while (cursor && cursor <= end) {
    const left = ((cursor.getTime() - min) / span) * 100;
    marks.push({
      key: isoDate(cursor),
      label: cursor.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }),
      pct: left,
      left: `${Math.max(left, 0)}%`
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return marks;
});

// UX: Mindestabstand zwischen Monatsköpfen – bei wenig Platz nur jeden n-ten Monat beschriften.
const visibleTicks = computed(() => {
  if (ticks.value.length < 2) return ticks.value;
  const gapPx = (trackPx.value / ticks.value.length);
  const step = Math.max(1, Math.ceil(MIN_TICK_GAP_PX / Math.max(gapPx, 1)));
  return ticks.value.filter((_, index) => index % step === 0);
});

function fits(bar) {
  const widthPx = (bar.widthPct / 100) * trackPx.value;
  return widthPx >= bar.label.length * CHAR_PX + 18;
}

function pack(bars) {
  const sorted = [...bars].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
  const trackEnds = [];
  sorted.forEach((bar) => {
    let track = trackEnds.findIndex((end) => end <= bar.leftPct);
    if (track < 0) {
      track = trackEnds.length;
      trackEnds.push(bar.leftPct + bar.widthPct);
    } else {
      trackEnds[track] = bar.leftPct + bar.widthPct;
    }
    bar.top = `${4 + track * 28}px`;
  });
  return { bars: sorted, trackCount: Math.max(trackEnds.length, 1) };
}

const printTitleMap = computed(() => {
  const map = {};
  (props.printTitles || []).forEach((item) => {
    if (item.short_name && item.name) map[item.short_name] = item.name;
  });
  return map;
});

// UX: Anzeigename pro Objekt, technischer/voller Name im Tooltip.
function displayTitle(item, channel, mailingNames) {
  const title = item.title || '';
  if (channel === 'email') return mailingNames[String(item.id ?? title)]?.label || title;
  if (channel === 'print') {
    const [short, ...rest] = title.split(' ');
    const full = printTitleMap.value[short];
    return full ? [full, ...rest].join(' ') : title;
  }
  return humanizeName(title);
}

const lanes = computed(() => {
  if (!bounds.value || !datedItems.value.length) return [];
  const { min, span } = bounds.value;
  const minBarPct = (MIN_BAR_PX / trackPx.value) * 100;
  const emailItems = datedItems.value.filter((item) => item.channel === 'email');
  const mailingNames = mailingDisplayNames(emailItems.map((item) => ({ id: item.id ?? item.title, name: item.title, sent_at: item.date })));

  return CHANNEL_ORDER.map((channel) => {
    const grouped = {};
    datedItems.value
      .filter((item) => item.channel === channel.code)
      .forEach((item) => {
        const hasRange = item.end_date && item.end_date !== item.date;
        const key = hasRange
          ? `range-${item.measure_id || item.title}-${isoDate(item.date)}`
          : `point-${isoDate(item.date)}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      });

    const route = CHANNEL_ROUTES[channel.code];
    const unpacked = Object.values(grouped).map((group) => {
      const first = group[0];
      const start = parseDate(first.date);
      const rawEnd = parseDate(first.end_date) || addDays(first.date, 1) || start;
      const startMs = start.getTime();
      const endMs = Math.max(rawEnd.getTime(), startMs + 86400000);
      const rawLeft = Math.max(((startMs - min) / span) * 100, 0);
      const widthPct = Math.min(Math.max(((endMs - startMs) / span) * 100, minBarPct), 100);
      // Kurze Balken am rechten Rand nach links schieben statt überlaufen lassen.
      const leftPct = Math.min(rawLeft, 100 - widthPct);
      const names = group.map((item) => displayTitle(item, channel.code, mailingNames)).filter(Boolean);
      const fullNames = group.map((item) => item.title).filter(Boolean);
      const label = group.length > 1 ? `${group.length} Maßnahmen` : (names[0] || channel.label);
      const dateLabel = first.end_date && first.end_date !== first.date
        ? `${formatDate(first.date)} – ${formatDate(first.end_date)}`
        : formatDate(first.date);
      const query = queryFor(route);
      if (first.measure_id) query.measure_id = String(first.measure_id);
      const tooltipLines = group.length > 1
        ? fullNames.map((name, index) => (names[index] !== name ? `${names[index]} (${name})` : name))
        : [fullNames[0] && fullNames[0] !== label ? `Technischer Name: ${fullNames[0]}` : ''];
      return {
        key: `${channel.code}-${keyFor(first, group)}`,
        startMs,
        endMs,
        leftPct,
        widthPct,
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        label,
        tooltip: [`${channel.label}: ${label}`, dateLabel, ...tooltipLines].filter(Boolean).join('\n'),
        to: route ? { name: route, query } : { path: '/marketing', query }
      };
    });

    const packed = pack(unpacked);
    return {
      ...channel,
      color: CHANNEL_COLORS[channel.code],
      to: route ? { name: route, query: queryFor(route) } : { path: '/marketing' },
      bars: packed.bars,
      trackCount: packed.trackCount
    };
  }).filter((lane) => lane.bars.length);
});

function keyFor(first, group) {
  return [
    isoDate(first.date),
    isoDate(first.end_date) || 'point',
    first.measure_id || group.map((item) => item.title).join('-')
  ].join('-');
}
</script>
